/**
 * 声音克隆 Tab - 多音色版本
 * 支持一个身份拥有多个情绪音色（正常、温柔、生气、唱歌等）
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import {
  Mic2,
  Upload,
  Play,
  Pause,
  Trash2,
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AudioWaveform,
  Plus,
  Star,
  RotateCw,
} from 'lucide-react'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService, uploadVoiceSampleByVoice } from '@/modules/identity/services'
import {
  VoicePublishDialog,
  VoiceModelCard,
  VoiceModelCardSkeleton,
  VoiceModelDeleteDialog,
  VoiceModelRenameDialog,
  OrphanedVoiceCard,
  OrphanedVoiceHeader,
} from '@/modules/identity/components'
import type {
  VoiceStatus,
  Voice,
  VoiceScene,
  VoiceSample,
  VoiceSampleSummary,
  VoiceProvider,
  VoiceTrainingModel,
  OrphanedVoice,
  VoiceSyncStatus,
  TrainingPhase,
  TrainingProgressData,
  TrainingCompleteData,
  TrainingErrorData,
  // v1.7 新增
  VoiceTrainingModelOption,
  // v10.4 新增：情感指令
  VoiceInstructionsConfig,
} from '@/modules/identity/types'
import {
  formatDuration,
  formatTotalDuration,
  VOICE_PROVIDER_CONFIG,
  VOICE_SCENE_CONFIG,
  TRAINING_PHASE_CONFIG,
} from '@/modules/identity/types'
import { getVoiceTrainingModels, getVoiceInstructions } from '@/modules/identity/services/identity-api'
import { Progress } from '@/shared/components/ui/progress'

interface VoiceTabProps {
  identityId: string
  voiceStatus: VoiceStatus
  onStatusChange?: () => void
}

export function VoiceTab({ identityId, voiceStatus, onStatusChange }: VoiceTabProps) {
  const { showSuccess, showError } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)

  // 音色列表状态
  const [voices, setVoices] = useState<Voice[]>([])
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null)
  const [loadingVoices, setLoadingVoices] = useState(true)

  // 当前音色的样本状态
  const [samples, setSamples] = useState<VoiceSample[]>([])
  const [summary, setSummary] = useState<VoiceSampleSummary | null>(null)
  const [loadingSamples, setLoadingSamples] = useState(false)

  // 操作状态
  const [uploading, setUploading] = useState(false)
  const [training, setTraining] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // v10.1 新增：取消发布状态
  const [unpublishing, setUnpublishing] = useState(false)

  // 弹窗状态
  const [createVoiceDialogOpen, setCreateVoiceDialogOpen] = useState(false)
  const [deleteVoiceDialogOpen, setDeleteVoiceDialogOpen] = useState(false)
  const [deleteSampleDialogOpen, setDeleteSampleDialogOpen] = useState(false)
  const [sampleToDelete, setSampleToDelete] = useState<VoiceSample | null>(null)
  const [voiceToDelete, setVoiceToDelete] = useState<Voice | null>(null)
  const [creatingVoice, setCreatingVoice] = useState(false)
  // v10.1 新增：发布弹窗状态
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [voiceToPublish, setVoiceToPublish] = useState<Voice | null>(null)

  // v10.2 新增：训练模型历史管理状态
  const [trainingModels, setTrainingModels] = useState<VoiceTrainingModel[]>([])
  const [loadingModels, setLoadingModels] = useState(false)
  const [modelCosyvoiceStatus, setModelCosyvoiceStatus] = useState<Record<string, 'OK' | 'DEPLOYING' | 'UNDEPLOYED'>>({})
  const [orphanedVoices, setOrphanedVoices] = useState<OrphanedVoice[]>([])
  const [syncingStatus, setSyncingStatus] = useState(false)
  const [activatingModel, setActivatingModel] = useState<string | null>(null)
  const [renamingModel, setRenamingModel] = useState<string | null>(null)
  const [deletingModel, setDeletingModel] = useState<string | null>(null)
  const [deletingOrphaned, setDeletingOrphaned] = useState<string | null>(null)
  
  // v10.2 新增：弹窗状态
  const [modelToDelete, setModelToDelete] = useState<VoiceTrainingModel | null>(null)
  const [modelToRename, setModelToRename] = useState<VoiceTrainingModel | null>(null)
  const [deleteModelDialogOpen, setDeleteModelDialogOpen] = useState(false)
  const [renameModelDialogOpen, setRenameModelDialogOpen] = useState(false)

  // 创建音色表单
  const [newVoiceName, setNewVoiceName] = useState('')
  const [newVoiceScene, setNewVoiceScene] = useState<VoiceScene>('normal')

  // 训练配置
  const [selectedProvider, setSelectedProvider] = useState<VoiceProvider>('cosyvoice')
  // v1.7 新增：训练模型选择
  const [availableTrainingModels, setAvailableTrainingModels] = useState<VoiceTrainingModelOption[]>([])
  const [selectedTrainingModel, setSelectedTrainingModel] = useState<string>('cosyvoice-v3-flash')  // 默认值

  // v10.4 新增：情感指令配置
  const [voiceInstructionsConfig, setVoiceInstructionsConfig] = useState<VoiceInstructionsConfig | null>(null)

  // 试听状态
  const [isPlaying, setIsPlaying] = useState(false)
  const [playingSampleId, setPlayingSampleId] = useState<string | null>(null)

  // v10.3 新增：训练进度 SSE 状态
  const [trainingPhase, setTrainingPhase] = useState<TrainingPhase | null>(null)
  const [trainingPercent, setTrainingPercent] = useState(0)
  const [trainingMessage, setTrainingMessage] = useState('')
  const trainingEventSourceRef = useRef<EventSource | null>(null)

  // ==================== 数据加载 ====================
  const loadVoices = useCallback(async () => {
    setLoadingVoices(true)
    try {
      const response = await IdentityApiService.listVoices(identityId)
      // 防御性编程：后端可能返回数组、{ data: [] } 或 { items: [] }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const responseData = response.data as any
      let voiceList: Voice[] = []
      if (Array.isArray(responseData)) {
        voiceList = responseData
      } else if (responseData?.data && Array.isArray(responseData.data)) {
        voiceList = responseData.data
      } else if (responseData?.items && Array.isArray(responseData.items)) {
        voiceList = responseData.items
      }
      setVoices(voiceList)
      // 自动选择默认音色或第一个
      const defaultVoice = voiceList.find(v => v.is_default) || voiceList[0]
      if (defaultVoice && (!selectedVoice || !voiceList.find(v => v.id === selectedVoice.id))) {
        setSelectedVoice(defaultVoice)
      }
    } catch (error) {
      console.error('Failed to load voices:', error)
      // 如果多音色 API 不存在，降级处理
      setVoices([])
    } finally {
      setLoadingVoices(false)
    }
  }, [identityId, selectedVoice])

  const loadSamples = useCallback(async () => {
    if (!selectedVoice) {
      setSamples([])
      setSummary(null)
      return
    }

    setLoadingSamples(true)
    try {
      const response = await IdentityApiService.listVoiceSamplesByVoice(
        identityId,
        selectedVoice.id
      )
      // 防御性编程：后端可能返回 items 或 data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const responseData = response.data as any
      setSamples(responseData?.data || responseData?.items || [])
      setSummary(responseData?.summary || null)
    } catch (error) {
      console.error('Failed to load samples:', error)
      // 降级：尝试老接口
      try {
        const response = await IdentityApiService.listVoiceSamples(identityId)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const responseData = response.data as any
        setSamples(responseData?.data || responseData?.items || [])
        setSummary(responseData?.summary || null)
      } catch {
        setSamples([])
        setSummary(null)
      }
    } finally {
      setLoadingSamples(false)
    }
  }, [identityId, selectedVoice])

  useEffect(() => {
    loadVoices()
  }, [loadVoices])

  // ==================== v10.2 训练模型管理 ====================
  
  /**
   * 加载训练模型历史
   */
  const loadTrainingModels = useCallback(async () => {
    if (!selectedVoice) {
      setTrainingModels([])
      return
    }

    setLoadingModels(true)
    try {
      const response = await IdentityApiService.listVoiceModels(identityId, selectedVoice.id)
      const items = response.data.items || []
      const activeModelId = response.data.active_model_id

      // v10.2 旧数据兼容性处理：
      // 当 items 为空但 active_model_id 存在时，创建一个遗留音色卡片
      // 注意：需要检查音色状态，failed 状态不应创建可试听的卡片
      if (items.length === 0 && activeModelId && selectedVoice.status !== 'failed') {
        const legacyModel: VoiceTrainingModel = {
          model_id: activeModelId,
          name: '遗留音色 (v10.2 之前)',
          target_model: 'cosyvoice-v3',
          trained_at: selectedVoice.created_at || new Date().toISOString(),
          is_active: true,
          // 根据音色状态设置 cosyvoice_status
          cosyvoice_status: selectedVoice.status === 'published' || selectedVoice.status === 'ready' ? 'OK' : 'DEPLOYING',
        }
        setTrainingModels([legacyModel])
      } else {
        setTrainingModels(items)
      }
    } catch (error) {
      console.error('Failed to load training models:', error)
      setTrainingModels([])
    } finally {
      setLoadingModels(false)
    }
  }, [identityId, selectedVoice])

  // 选中音色变化时加载样本和训练模型
  useEffect(() => {
    if (selectedVoice) {
      loadSamples()
      loadTrainingModels()
    }
  }, [selectedVoice, loadSamples, loadTrainingModels])

  // v1.7: 加载训练模型配置
  // v1.7 + v10.4: 加载训练模型配置和情感指令配置
  useEffect(() => {
    async function loadModelsConfig() {
      try {
        const models = await getVoiceTrainingModels()
        // 目前只支持 cosyvoice 提供商
        if (models.cosyvoice && models.cosyvoice.length > 0) {
          setAvailableTrainingModels(models.cosyvoice)
          // 设置默认模型
          const defaultModel = models.cosyvoice.find(m => m.is_default)
          if (defaultModel) {
            setSelectedTrainingModel(defaultModel.model_id)
          }
        }
      } catch (error) {
        console.warn('Failed to load training models config:', error)
        // 使用硬编码默认值作为降级
        setAvailableTrainingModels([
          { model_id: 'cosyvoice-v3-flash', name: 'CosyVoice V3 Flash', is_default: true },
        ])
      }
    }
    
    // v10.4: 加载情感指令配置
    async function loadInstructionsConfig() {
      try {
        const config = await getVoiceInstructions()
        setVoiceInstructionsConfig(config)
      } catch (error) {
        console.warn('Failed to load voice instructions config:', error)
        // 不影响主功能，静默失败
      }
    }
    
    loadModelsConfig()
    loadInstructionsConfig()
  }, [])

  /**
   * SSE 同步 CosyVoice 状态
   */
  const handleSyncCosyVoiceStatus = useCallback(async () => {
    setSyncingStatus(true)
    setOrphanedVoices([])
    const newStatusMap: Record<string, 'OK' | 'DEPLOYING' | 'UNDEPLOYED'> = {}
    const newOrphaned: OrphanedVoice[] = []

    try {
      const sseUrl = IdentityApiService.getCosyVoiceSyncUrl(identityId)
      const eventSource = new EventSource(sseUrl, { withCredentials: true })

      await new Promise<void>((resolve, _reject) => {
        eventSource.addEventListener('voice_status', (event) => {
          try {
            const data = JSON.parse(event.data) as VoiceSyncStatus
            if (data.in_system) {
              newStatusMap[data.model_id] = data.status
            } else {
              newOrphaned.push({ model_id: data.model_id, status: data.status })
            }
          } catch (e) {
            console.error('Failed to parse SSE data:', e)
          }
        })

        eventSource.addEventListener('sync_complete', (event) => {
          console.log('Sync complete:', event.data)
          eventSource.close()
          resolve()
        })

        eventSource.addEventListener('error', (event) => {
          console.error('SSE error:', event)
          eventSource.close()
          // 不 reject，可能是正常关闭
          resolve()
        })

        // 超时保护
        setTimeout(() => {
          eventSource.close()
          resolve()
        }, 30000)
      })

      setModelCosyvoiceStatus(newStatusMap)
      setOrphanedVoices(newOrphaned)
      showSuccess('状态同步完成', '同步成功')
    } catch (error) {
      console.error('Failed to sync CosyVoice status:', error)
      showError('同步失败', '操作失败')
    } finally {
      setSyncingStatus(false)
    }
  }, [identityId, showSuccess, showError])

  /**
   * 激活训练模型
   */
  const handleActivateModel = useCallback(async (modelId: string) => {
    if (!selectedVoice) return

    setActivatingModel(modelId)
    try {
      await IdentityApiService.activateVoiceModel(identityId, selectedVoice.id, modelId)
      showSuccess('已设置为使用中', '操作成功')
      loadTrainingModels()
      loadVoices()
    } catch (error) {
      console.error('Failed to activate model:', error)
      showError('设置失败', '操作失败')
    } finally {
      setActivatingModel(null)
    }
  }, [identityId, selectedVoice, showSuccess, showError, loadTrainingModels, loadVoices])

  /**
   * 打开重命名弹窗
   */
  const handleRenameModelClick = (modelId: string) => {
    const model = trainingModels.find(m => m.model_id === modelId)
    if (model) {
      setModelToRename(model)
      setRenameModelDialogOpen(true)
    }
  }

  /**
   * 确认重命名
   */
  const handleRenameModelConfirm = useCallback(async (newName: string) => {
    if (!selectedVoice || !modelToRename) return

    setRenamingModel(modelToRename.model_id)
    try {
      await IdentityApiService.renameVoiceModel(
        identityId,
        selectedVoice.id,
        modelToRename.model_id,
        { name: newName }
      )
      showSuccess('重命名成功', '操作成功')
      setRenameModelDialogOpen(false)
      setModelToRename(null)
      loadTrainingModels()
    } catch (error) {
      console.error('Failed to rename model:', error)
      showError('重命名失败', '操作失败')
    } finally {
      setRenamingModel(null)
    }
  }, [identityId, selectedVoice, modelToRename, showSuccess, showError, loadTrainingModels])

  /**
   * 打开删除弹窗
   */
  const handleDeleteModelClick = (modelId: string) => {
    const model = trainingModels.find(m => m.model_id === modelId)
    if (model) {
      setModelToDelete(model)
      setDeleteModelDialogOpen(true)
    }
  }

  /**
   * 确认删除训练模型
   */
  const handleDeleteModelConfirm = useCallback(async () => {
    if (!selectedVoice || !modelToDelete) return

    setDeletingModel(modelToDelete.model_id)
    try {
      const result = await IdentityApiService.deleteVoiceModel(
        identityId,
        selectedVoice.id,
        modelToDelete.model_id
      )
      
      if (!result.data.cosyvoice_deleted) {
        showSuccess('模型已从系统删除（CosyVoice 平台删除失败，请手动清理）', '部分成功')
      } else {
        showSuccess('模型已删除', '删除成功')
      }
      
      setDeleteModelDialogOpen(false)
      setModelToDelete(null)
      loadTrainingModels()
      loadVoices()
    } catch (error) {
      console.error('Failed to delete model:', error)
      showError('删除失败', '操作失败')
    } finally {
      setDeletingModel(null)
    }
  }, [identityId, selectedVoice, modelToDelete, showSuccess, showError, loadTrainingModels, loadVoices])

  /**
   * 试听指定模型
   */
  const handlePreviewModel = useCallback(async (modelId: string, text: string): Promise<string> => {
    if (!selectedVoice) throw new Error('未选择音色')

    const response = await IdentityApiService.previewVoice(identityId, selectedVoice.id, {
      text,
      model_id: modelId,
    })
    return response.data.audio_url
  }, [identityId, selectedVoice])

  /**
   * 删除孤儿音色
   */
  const handleDeleteOrphanedVoice = useCallback(async (modelId: string) => {
    setDeletingOrphaned(modelId)
    try {
      await IdentityApiService.deleteOrphanedVoice(identityId, modelId)
      showSuccess('孤儿音色已删除', '删除成功')
      setOrphanedVoices(prev => prev.filter(v => v.model_id !== modelId))
    } catch (error) {
      console.error('Failed to delete orphaned voice:', error)
      showError('删除失败', '操作失败')
    } finally {
      setDeletingOrphaned(null)
    }
  }, [identityId, showSuccess, showError])

  // ==================== 音色操作 ====================
  const handleCreateVoice = useCallback(async () => {
    if (!newVoiceName.trim()) {
      showError('请输入音色名称', '验证失败')
      return
    }

    setCreatingVoice(true)
    try {
      const response = await IdentityApiService.createVoice(identityId, {
        name: newVoiceName.trim(),
        scene: newVoiceScene,
        is_default: voices.length === 0,
      })
      showSuccess(`音色「${response.data.name}」已创建`, '创建成功')
      setCreateVoiceDialogOpen(false)
      setNewVoiceName('')
      setNewVoiceScene('normal')
      loadVoices()
      // 选中新创建的音色
      setSelectedVoice(response.data)
    } catch (error) {
      console.error('Failed to create voice:', error)
      showError('创建失败', '操作失败')
    } finally {
      setCreatingVoice(false)
    }
  }, [identityId, newVoiceName, newVoiceScene, voices.length, showSuccess, showError, loadVoices])

  const handleSetDefault = useCallback(async (voice: Voice) => {
    try {
      await IdentityApiService.setDefaultVoice(identityId, voice.id)
      showSuccess(`已将「${voice.name}」设为默认`, '设置成功')
      loadVoices()
    } catch (error) {
      console.error('Failed to set default:', error)
      showError('设置失败', '操作失败')
    }
  }, [identityId, showSuccess, showError, loadVoices])

  const handleDeleteVoiceClick = (voice: Voice) => {
    // 默认音色不能删除，删除按钮已在 UI 层面隐藏
    if (voice.is_default) return
    setVoiceToDelete(voice)
    setDeleteVoiceDialogOpen(true)
  }

  const handleDeleteVoiceConfirm = useCallback(async () => {
    if (!voiceToDelete) return

    setDeleting(true)
    try {
      await IdentityApiService.deleteVoice(identityId, voiceToDelete.id)
      showSuccess(`音色「${voiceToDelete.name}」已删除`, '删除成功')
      setDeleteVoiceDialogOpen(false)
      setVoiceToDelete(null)
      loadVoices()
      if (selectedVoice?.id === voiceToDelete.id) {
        setSelectedVoice(null)
      }
    } catch (error) {
      console.error('Failed to delete voice:', error)
      showError('删除失败', '操作失败')
    } finally {
      setDeleting(false)
    }
  }, [identityId, voiceToDelete, selectedVoice, showSuccess, showError, loadVoices])

  // ==================== v10.1 发布/取消发布 ====================
  
  /**
   * 打开发布弹窗
   */
  const handlePublishVoiceClick = (voice: Voice) => {
    setVoiceToPublish(voice)
    setPublishDialogOpen(true)
  }

  /**
   * 取消发布音色
   */
  const handleUnpublishVoice = useCallback(async (voice: Voice) => {
    setUnpublishing(true)
    try {
      await IdentityApiService.unpublishVoice(identityId, voice.id)
      showSuccess(`音色「${voice.name}」已取消发布`, '操作成功')
      loadVoices()
    } catch (error: unknown) {
      console.error('Failed to unpublish voice:', error)
      // 检查是否是 VOICE_IN_USE 错误
      const err = error as { response?: { data?: { error?: string; bound_facets?: string[] } } }
      if (err?.response?.data?.error === 'VOICE_IN_USE') {
        const boundFacets = err.response.data.bound_facets || []
        showError(
          `无法取消发布：该音色已被以下场景绑定\n${boundFacets.join('、')}`,
          '操作失败'
        )
      } else {
        showError('取消发布失败', '操作失败')
      }
    } finally {
      setUnpublishing(false)
    }
  }, [identityId, showSuccess, showError, loadVoices])

  /**
   * 发布弹窗确认后的回调
   */
  const handlePublishSuccess = useCallback(() => {
    setPublishDialogOpen(false)
    setVoiceToPublish(null)
    loadVoices()
  }, [loadVoices])

  // ==================== 上传音频 ====================
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0 || !selectedVoice) return

    setUploading(true)
    let successCount = 0
    let failCount = 0

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('audio/')) {
        showError(`${file.name} 不是音频文件`, '文件类型错误')
        failCount++
        continue
      }

      if (file.size > 50 * 1024 * 1024) {
        showError(`${file.name} 超过 50MB 限制`, '文件过大')
        failCount++
        continue
      }

      try {
        const duration = await getAudioDuration(file)
        await uploadVoiceSampleByVoice(identityId, selectedVoice.id, file, duration)
        successCount++
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error)
        failCount++
      }
    }

    if (successCount > 0) {
      showSuccess(`成功上传 ${successCount} 个音频`, '上传成功')
      loadSamples()
      onStatusChange?.()
    }
    if (failCount > 0) {
      showError(`${failCount} 个文件上传失败`, '部分失败')
    }

    setUploading(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const getAudioDuration = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const audio = new Audio()
      audio.onloadedmetadata = () => resolve(audio.duration)
      audio.onerror = () => resolve(0)
      audio.src = URL.createObjectURL(file)
    })
  }

  // ==================== 删除样本 ====================
  const handleDeleteSampleClick = (sample: VoiceSample) => {
    setSampleToDelete(sample)
    setDeleteSampleDialogOpen(true)
  }

  const handleDeleteSampleConfirm = useCallback(async () => {
    if (!sampleToDelete || !selectedVoice) return

    setDeleting(true)
    try {
      await IdentityApiService.deleteVoiceSampleByVoice(identityId, selectedVoice.id, sampleToDelete.id)
      showSuccess('音频已删除', '删除成功')
      setDeleteSampleDialogOpen(false)
      setSampleToDelete(null)
      loadSamples()
      onStatusChange?.()
    } catch (error) {
      console.error('Failed to delete sample:', error)
      showError('删除失败', '操作失败')
    } finally {
      setDeleting(false)
    }
  }, [identityId, selectedVoice, sampleToDelete, showSuccess, showError, loadSamples, onStatusChange])

  // ==================== 开始训练 (v10.3 SSE) ====================
  const handleStartTraining = useCallback(() => {
    if (!selectedVoice) {
      showError('请先选择一个音色', '未选择音色')
      return
    }
    if (!summary || summary.passed_count < 3) {
      showError('至少需要 3 个有效音频样本', '样本不足')
      return
    }

    // 关闭之前的 SSE 连接
    if (trainingEventSourceRef.current) {
      trainingEventSourceRef.current.close()
    }

    setTraining(true)
    setTrainingPhase('preparing')
    setTrainingPercent(0)
    setTrainingMessage('准备中...')

    // 使用 SSE 接口
    const sseUrl = IdentityApiService.getTrainingProgressUrl(
      identityId,
      selectedVoice.id,
      selectedProvider,
      selectedTrainingModel  // v1.7: 使用选择的训练模型
    )
    const eventSource = new EventSource(sseUrl, { withCredentials: true })
    trainingEventSourceRef.current = eventSource

    // 监听进度事件
    eventSource.addEventListener('progress', (event) => {
      try {
        const data = JSON.parse(event.data) as TrainingProgressData
        setTrainingPhase(data.phase)
        setTrainingPercent(data.percent)
        setTrainingMessage(data.message)
      } catch (e) {
        console.error('Failed to parse progress event:', e)
      }
    })

    // 监听完成事件
    eventSource.addEventListener('complete', (event) => {
      try {
        const data = JSON.parse(event.data) as TrainingCompleteData
        showSuccess(data.message || '训练完成', '训练成功')
        eventSource.close()
        trainingEventSourceRef.current = null
        setTraining(false)
        setTrainingPhase(null)
        setTrainingPercent(0)
        setTrainingMessage('')
        // 刷新数据
        loadVoices()
        loadTrainingModels()
        onStatusChange?.()
      } catch (e) {
        console.error('Failed to parse complete event:', e)
      }
    })

    // 监听错误事件
    eventSource.addEventListener('error', (event) => {
      // 尝试解析错误数据
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const eventWithData = event as any
        if (eventWithData.data) {
          const data = JSON.parse(eventWithData.data) as TrainingErrorData
          showError(data.message || '训练失败', data.code || '错误')
        } else {
          showError('训练连接中断', '连接错误')
        }
      } catch {
        showError('训练失败', '未知错误')
      }
      eventSource.close()
      trainingEventSourceRef.current = null
      setTraining(false)
      setTrainingPhase(null)
      setTrainingPercent(0)
      setTrainingMessage('')
      loadVoices()
    })

    // SSE 原生错误处理
    eventSource.onerror = () => {
      // 如果连接被正常关闭，不显示错误
      if (eventSource.readyState === EventSource.CLOSED) {
        return
      }
      showError('训练连接中断', '连接错误')
      eventSource.close()
      trainingEventSourceRef.current = null
      setTraining(false)
      setTrainingPhase(null)
      loadVoices()
    }
  }, [identityId, selectedVoice, selectedProvider, selectedTrainingModel, summary, showSuccess, showError, loadVoices, loadTrainingModels, onStatusChange])

  // ==================== 播放样本 ====================
  const handlePlaySample = (sample: VoiceSample) => {
    if (audioRef.current) {
      if (playingSampleId === sample.id && isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
        setPlayingSampleId(null)
      } else {
        audioRef.current.src = sample.file_url
        audioRef.current.play()
        setIsPlaying(true)
        setPlayingSampleId(sample.id)
      }
    }
  }

  const isTraining = voiceStatus === 'training' || selectedVoice?.status === 'training'

  // ==================== 渲染 ====================
  return (
    <div className="space-y-4">
      <audio
        ref={audioRef}
        onEnded={() => {
          setIsPlaying(false)
          setPlayingSampleId(null)
        }}
        onPause={() => setIsPlaying(false)}
      />

      {/* 音色管理卡片 */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mic2 className="h-5 w-5" />
                音色管理
              </CardTitle>
              <CardDescription>
                管理多个情绪音色（正常、温柔、生气、唱歌等）
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={() => setCreateVoiceDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              新增音色
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingVoices ? (
            <div className="text-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-violet-600" />
              <p className="text-sm text-slate-500">加载中...</p>
            </div>
          ) : voices.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Mic2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="mb-2">暂无音色</p>
              <p className="text-sm mb-4">创建第一个音色开始克隆声音</p>
              <Button onClick={() => setCreateVoiceDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                创建音色
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {voices.map((voice) => (
                <div
                  key={voice.id}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedVoice?.id === voice.id
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedVoice(voice)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg">
                      {VOICE_SCENE_CONFIG[voice.scene]?.icon || '🎤'}
                    </span>
                    <div className="flex items-center gap-1">
                      {voice.is_default && (
                        <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                      )}
                      {/* v10.1: 状态图标 */}
                      {voice.status === 'published' && (
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                      )}
                      {voice.status === 'ready' && (
                        <CheckCircle className="h-3.5 w-3.5 text-blue-500" />
                      )}
                      {voice.status === 'training' && (
                        <Loader2 className="h-3.5 w-3.5 text-violet-500 animate-spin" />
                      )}
                      {voice.status === 'failed' && (
                        <XCircle className="h-3.5 w-3.5 text-red-500" />
                      )}
                    </div>
                  </div>
                  <p className="font-medium text-sm truncate">{voice.name}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-slate-500">
                      {VOICE_SCENE_CONFIG[voice.scene]?.label || voice.scene}
                    </p>
                    {/* v10.1: 显示状态标签 */}
                    {voice.status === 'published' && (
                      <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700">已发布</Badge>
                    )}
                    {voice.status === 'ready' && (
                      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">已就绪</Badge>
                    )}
                  </div>
                  {/* v10.1: 已发布音色显示合成参数 */}
                  {voice.status === 'published' && (
                    <p className="text-xs text-slate-400 mt-1">
                      语速 {voice.speech_rate?.toFixed(1) || '1.0'} | 音调 {voice.pitch_rate?.toFixed(1) || '1.0'} | 音量 {voice.volume || 50}
                    </p>
                  )}
                  {selectedVoice?.id === voice.id && (
                    <div className="flex items-center gap-1 mt-2 pt-2 border-t flex-wrap">
                      {!voice.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs px-2"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSetDefault(voice)
                          }}
                        >
                          设为默认
                        </Button>
                      )}
                      {/* v10.1: ready 状态显示发布按钮 */}
                      {voice.status === 'ready' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePublishVoiceClick(voice)
                          }}
                        >
                          发布
                        </Button>
                      )}
                      {/* v10.1: published 状态显示取消发布按钮 */}
                      {voice.status === 'published' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleUnpublishVoice(voice)
                          }}
                          disabled={unpublishing}
                        >
                          {unpublishing ? '处理中...' : '取消发布'}
                        </Button>
                      )}
                      {/* 默认音色不能删除，已发布音色也不能删除 */}
                      {!voice.is_default && voice.status !== 'published' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs px-2 text-red-500 hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteVoiceClick(voice)
                          }}
                        >
                          删除
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 当前音色详情 */}
      {selectedVoice && (
        <>
          {/* 音频样本 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <span>{VOICE_SCENE_CONFIG[selectedVoice.scene]?.icon}</span>
                  {selectedVoice.name} - 音频样本
                  {summary && (
                    <Badge variant="secondary" className="text-xs">
                      {summary.total_count} 个 · {formatTotalDuration(summary.total_duration)}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadSamples()}
                    disabled={loadingSamples}
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingSamples ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || isTraining}
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    上传音频
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingSamples ? (
                <div className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-violet-600" />
                </div>
              ) : samples.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <AudioWaveform className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="mb-2">暂无音频样本</p>
                  <p className="text-sm">上传 3 个以上高质量音频开始训练</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {samples.map((sample) => (
                    <div
                      key={sample.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handlePlaySample(sample)}
                        >
                          {playingSampleId === sample.id && isPlaying ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]">
                            {sample.file_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDuration(sample.duration)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {sample.status === 'passed' && (
                          <Badge variant="secondary" className="bg-green-50 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            通过
                          </Badge>
                        )}
                        {sample.status === 'failed' && (
                          <Badge variant="secondary" className="bg-red-50 text-red-700">
                            <XCircle className="h-3 w-3 mr-1" />
                            {sample.failed_reason || '失败'}
                          </Badge>
                        )}
                        {sample.status === 'processing' && (
                          <Badge variant="secondary" className="animate-pulse">
                            <Clock className="h-3 w-3 mr-1" />
                            检测中
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => handleDeleteSampleClick(sample)}
                          disabled={isTraining}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* v10.2 新增：已训练模型列表 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  📋 已训练模型
                  {trainingModels.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {trainingModels.length} 个
                    </Badge>
                  )}
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSyncCosyVoiceStatus}
                  disabled={syncingStatus}
                >
                  {syncingStatus ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RotateCw className="h-4 w-4 mr-2" />
                  )}
                  刷新状态
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingModels ? (
                <div className="space-y-3">
                  <VoiceModelCardSkeleton />
                  <VoiceModelCardSkeleton />
                </div>
              ) : trainingModels.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <AudioWaveform className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="mb-2">暂无训练模型</p>
                  <p className="text-sm">上传音频样本并开始训练后，模型将显示在这里</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {trainingModels.map((model) => (
                    <VoiceModelCard
                      key={model.model_id}
                      model={model}
                      cosyvoiceStatus={modelCosyvoiceStatus[model.model_id] || 'OK'}
                      onPreview={handlePreviewModel}
                      onActivate={handleActivateModel}
                      onRename={handleRenameModelClick}
                      onDelete={handleDeleteModelClick}
                      isActivating={activatingModel === model.model_id}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* v10.2 新增：孤儿音色区域 */}
          {orphanedVoices.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader>
                <OrphanedVoiceHeader count={orphanedVoices.length} />
                <CardDescription>
                  以下音色存在于 CosyVoice 平台，但系统中没有对应记录
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {orphanedVoices.map((voice) => (
                    <OrphanedVoiceCard
                      key={voice.model_id}
                      voice={voice}
                      onDelete={handleDeleteOrphanedVoice}
                      isDeleting={deletingOrphaned === voice.model_id}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 训练控制 - v1.7: 移除状态限制，已训练也可以重新训练 */}
          {selectedVoice.status !== 'training' && !training && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {selectedVoice.status === 'ready' || selectedVoice.status === 'published' ? '重新训练' : '开始训练'}
                </CardTitle>
                <CardDescription>
                  {selectedVoice.status === 'ready' || selectedVoice.status === 'published' 
                    ? `使用新样本重新训练「${selectedVoice.name}」，将生成新的音色模型`
                    : `训练「${selectedVoice.name}」音色模型`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* 服务提供商 */}
                <div className="space-y-2">
                  <Label>服务提供商</Label>
                  <Select
                    value={selectedProvider}
                    onValueChange={(v) => setSelectedProvider(v as VoiceProvider)}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(VOICE_PROVIDER_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.label} - {config.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* v1.7: 训练模型选择 */}
                {availableTrainingModels.length > 0 && (
                  <div className="space-y-2">
                    <Label>训练模型</Label>
                    <Select
                      value={selectedTrainingModel}
                      onValueChange={setSelectedTrainingModel}
                    >
                      <SelectTrigger className="w-[280px]">
                        <SelectValue placeholder="选择训练模型" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTrainingModels.map((model) => (
                          <SelectItem key={model.model_id} value={model.model_id}>
                            <div className="flex flex-col">
                              <span>{model.name}</span>
                              {model.description && (
                                <span className="text-xs text-slate-500">{model.description}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button
                  onClick={handleStartTraining}
                  disabled={training || !summary || summary.passed_count < 3}
                >
                  {training && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {selectedVoice.status === 'ready' || selectedVoice.status === 'published' ? (
                    <>
                      <RotateCw className="h-4 w-4 mr-2" />
                      重新训练
                    </>
                  ) : '开始训练'}
                </Button>
                {summary && summary.passed_count < 3 && (
                  <p className="text-xs text-amber-600">
                    ⚠️ 至少需要 3 个有效音频样本才能开始训练
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* v10.3 训练进度 - 使用进度条和阶段提示 */}
          {(selectedVoice.status === 'training' || training) && (
            <Card className="border-violet-200 bg-violet-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-violet-700">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  正在训练「{selectedVoice.name}」
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* 进度条 */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">
                      {trainingPhase && TRAINING_PHASE_CONFIG[trainingPhase]
                        ? `${TRAINING_PHASE_CONFIG[trainingPhase].icon} ${TRAINING_PHASE_CONFIG[trainingPhase].label}`
                        : '准备中...'}
                    </span>
                    <span className="font-medium text-violet-600">{trainingPercent}%</span>
                  </div>
                  <Progress value={trainingPercent} className="h-2" />
                </div>
                {/* 状态消息 */}
                {trainingMessage && (
                  <p className="text-sm text-slate-500 text-center">{trainingMessage}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* v10.3: 移除独立的"声音试听"卡片 - 已训练模型列表中已有试听按钮 */}
        </>
      )}

      {/* 创建音色弹窗 */}
      <Dialog open={createVoiceDialogOpen} onOpenChange={setCreateVoiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>创建新音色</DialogTitle>
            <DialogDescription>
              为不同情绪场景创建独立的声音模型
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>音色名称 *</Label>
              <Input
                value={newVoiceName}
                onChange={(e) => setNewVoiceName(e.target.value)}
                placeholder="如：温柔撒娇、生气训斥"
                maxLength={30}
              />
            </div>
            <div className="space-y-2">
              <Label>使用场景</Label>
              <Select
                value={newVoiceScene}
                onValueChange={(v) => setNewVoiceScene(v as VoiceScene)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VOICE_SCENE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.icon} {config.label} - {config.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateVoiceDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleCreateVoice} disabled={creatingVoice}>
              {creatingVoice && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除音色确认 */}
      <AlertDialog open={deleteVoiceDialogOpen} onOpenChange={setDeleteVoiceDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除音色「{voiceToDelete?.name}」吗？所有关联的音频样本也将被删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteVoiceConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 删除样本确认 */}
      <AlertDialog open={deleteSampleDialogOpen} onOpenChange={setDeleteSampleDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除音频「{sampleToDelete?.file_name}」吗？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSampleConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* v10.1 新增：发布音色弹窗 */}
      <VoicePublishDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        voice={voiceToPublish}
        identityId={identityId}
        onPublishSuccess={handlePublishSuccess}
        // v10.4: 传入情感指令配置（targetModel 由弹窗自己加载，确保正确）
        instructionsConfig={voiceInstructionsConfig}
      />

      {/* v10.2 新增：删除训练模型确认弹窗 */}
      <VoiceModelDeleteDialog
        open={deleteModelDialogOpen}
        onOpenChange={setDeleteModelDialogOpen}
        model={modelToDelete}
        onConfirm={handleDeleteModelConfirm}
        isDeleting={!!deletingModel}
      />

      {/* v10.2 新增：重命名训练模型弹窗 */}
      <VoiceModelRenameDialog
        open={renameModelDialogOpen}
        onOpenChange={setRenameModelDialogOpen}
        model={modelToRename}
        onConfirm={handleRenameModelConfirm}
        isRenaming={!!renamingModel}
      />

    </div>
  )
}
